//path: src\types\animationTypes.ts

export interface SupabaseClipType {
  clip_type_id: string;
  clip_type_source_name: string;
  clip_type_display_name: string;
  clip_type_duration: number;
  clip_type_display_on: boolean;
  clip_type_category: string;
  clip_type_begin_time?: number;
  clip_type_end_time?: number;
  clip_type_scope?: {
    mask?: boolean;
    prop?: boolean;
    character?: boolean;
  };
  clip_type_settings?: string; // JSON string containing default settings
  asset_type_id?: string | null; // Optional asset_type_id to match clips to specific asset types
  scene_instance_id?: string | null; // Null = stock; set = imported for that scene
}

export interface MediaClipOverrides {
  sourceIn?: number;
  sourceDuration?: number;
  volume?: number;
}

export interface SupabaseClipInstance {
  id?: number;
  scene_instance_id: string;
  asset_instance_id: string | null;
  ui_element_instance_id?: string | null;
  clip_type_id: string;
  clip_instance_id: string;
  clip_instance_display_name: string;
  clip_instance_source_name: string;
  clip_instance_begin_time: number;
  clip_instance_end_time: number;
  clip_instance_duration: number;
  clip_instance_category: string;
  clip_instance_payload: object;
  clip_instance_speed?: number;
  clip_instance_volume?: number;
  clip_instance_settings?: Record<string, unknown> | string | null; // JSONB column; may arrive as object or legacy string
  // NULL = timeline clip; set = reactive (FK -> AssetInstance of category 'Trigger').
  trigger_asset_instance_id?: string | null;
}

export interface Clip {
  typeId: string;
  typeSourceName: string;
  typeDisplayName: string;
  typeDuration: number;
  displayOn: boolean;
  category: string;
  scope?: {
    mask?: boolean;
    prop?: boolean;
    props?: boolean;
    character?: boolean;
    characters?: boolean;
  };
  assetTypeId?: string | null; // Optional asset_type_id to match clips to specific asset types
  sceneInstanceId?: string | null; // Null = stock; set = imported for that scene
}

export interface ClipInstance {
  sceneInstanceId: string;
  assetId: string;
  uiElementId?: string | null;
  typeId: string;
  instanceId: string;
  instanceStartTime: number;
  instanceEndTime: number;
  instanceSourceName: string;
  instanceDisplayName: string;
  instanceDuration: number;
  instanceSpeed: number;
  instanceVolume?: number;
  instanceCategory: string;
  clipInstanceSettings?: Record<string, unknown> | string;
  rotationDirection?: { x: number; y: number; z: number } | string;
  scaleTo?: { x: number; y: number; z: number } | string;
  height?: number;
  // NULL = timeline clip; set = reactive (FK -> AssetInstance of category 'Trigger').
  triggerAssetInstanceId?: string | null;
}

// Per-clip reaction behaviour for reactive clips, persisted in clip_instance_settings.animationStatus.
export type AnimationStatus = 'Loop' | 'Freeze' | 'OneShot';
export const ANIMATION_STATUS_DEFAULT: AnimationStatus = 'Freeze';
// Freeze belongs to the Await shell. A slot holding a real animation defaults to playing it.
export const ANIMATION_STATUS_FILLED_DEFAULT: AnimationStatus = 'Loop';
export const ANIMATION_STATUS_ORDER: AnimationStatus[] = ['Freeze', 'Loop', 'OneShot'];
// All three describe the wait: what the asset does from the moment the playhead
// reaches the gate until the viewer fires it and the asset resumes its timeline.
export const ANIMATION_STATUS_LABELS: Record<AnimationStatus, string> = {
  Freeze: 'Holds still',
  Loop: 'Plays on a loop',
  OneShot: 'Plays once',
};

export const CLIP_CATEGORIES = {
  TWEEN: 'tween',
  SNAP: 'snap',
  WALK: 'locomotion',
  AUDIO: 'audio',
  VIDEO: 'video',
  TRIGGER: 'trigger'
} as const;

/**
 * Snap clips share tween's behaviour (no default looping, easing-driven, no
 * speed-based duration recalc) but are gated to Spectacles scenes in the UI.
 * Treat tween and snap categories interchangeably for those behavioural sites.
 */
export function isTweenLikeCategory(category: string | undefined | null): boolean {
  return category === CLIP_CATEGORIES.TWEEN || category === CLIP_CATEGORIES.SNAP;
}

/**
 * Visual height of a snap clip on the timeline (stop-sign octagon).
 * The octagon stretches horizontally to fit the clip's display name; only
 * the height is fixed. Hit-testing uses this as a conservative minimum
 * horizontal region from the clip's start, with the full visible width
 * caught via DOM-based detection on the clip wrapper.
 */
export const SNAP_OCTAGON_HEIGHT_PX = 22;

export const CLIP_SOURCES = {
  FADE_IN: 'Tween-FadeIn',
  FADE_OUT: 'Tween-FadeOut',
  WALK: 'go_to_position',
  AUDIO: 'audio',
  VIDEO: 'video'
} as const;

/**
 * Tween easing modes. Values mirror FlowEnt's `Easing` enum on the Unity side
 * (see Assets/Plugins/FlowEnt/Scripts/Runtime/Easings/Abstract/Easing.cs) — the
 * string values are serialized into `clip_instance_settings.easing` and parsed
 * directly into the enum via Enum.TryParse in BaseTween.ApplyBaseSettings.
 */
export const TWEEN_EASINGS = [
  'Linear',
  'EaseInSine', 'EaseOutSine', 'EaseInOutSine',
  'EaseInQuad', 'EaseOutQuad', 'EaseInOutQuad',
  'EaseInCubic', 'EaseOutCubic', 'EaseInOutCubic',
  'EaseInQuart', 'EaseOutQuart', 'EaseInOutQuart',
  'EaseInQuint', 'EaseOutQuint', 'EaseInOutQuint',
  'EaseInExpo', 'EaseOutExpo', 'EaseInOutExpo',
  'EaseInCirc', 'EaseOutCirc', 'EaseInOutCirc',
  'EaseInBack', 'EaseOutBack', 'EaseInOutBack',
  'EaseInElastic', 'EaseOutElastic', 'EaseInOutElastic',
  'EaseInBounce', 'EaseOutBounce', 'EaseInOutBounce',
  'Bounce',
  'Shake'
] as const;

export type TweenEasing = typeof TWEEN_EASINGS[number];

export const DEFAULT_TWEEN_EASING: TweenEasing = 'Linear';

/** Curve segment of FlowEnt easing names → short readable noun for labels. */
const TWEEN_CURVE_DISPLAY: Record<string, string> = {
  Sine: 'sine',
  Quad: 'quadratic',
  Cubic: 'cubic',
  Quart: 'quartic',
  Quint: 'quintic',
  Expo: 'exponential',
  Circ: 'circular',
  Back: 'back overshoot',
  Elastic: 'elastic',
  Bounce: 'bounce'
};

/**
 * Human-readable label for dropdowns. Serialized `value` sent to Unity stays
 * the enum string (e.g. `EaseInQuad`); this is UI-only.
 */
export function getTweenEasingDisplayLabel(easing: string): string {
  if (easing === 'Linear') return 'Linear';
  if (easing === 'Shake') return 'Shake';
  if (easing === 'Bounce') return 'Bounce';
  const m = easing.match(/^Ease(In|Out|InOut)(Sine|Quad|Cubic|Quart|Quint|Expo|Circ|Back|Elastic|Bounce)$/);
  if (!m) return easing;
  const [, io, curve] = m;
  const curveLabel = TWEEN_CURVE_DISPLAY[curve] ?? curve.toLowerCase();
  const phase = io === 'In' ? 'Ease in' : io === 'Out' ? 'Ease out' : 'Ease in-out';
  return `${phase} · ${curveLabel}`;
}

export const TWEEN_EASING_SELECT_OPTIONS: { value: TweenEasing; label: string }[] = TWEEN_EASINGS.map((value) => ({
  value,
  label: getTweenEasingDisplayLabel(value)
}));

/** Options for a clip whose stored easing is not in the current enum list. */
export function getTweenEasingSelectOptions(currentValue: string): { value: string; label: string }[] {
  const base = TWEEN_EASING_SELECT_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  if (TWEEN_EASINGS.includes(currentValue as TweenEasing)) return base;
  return [{ value: currentValue, label: `${currentValue} (legacy)` }, ...base];
}

/** Legacy source names (pre-Tween rename). Use only for backward compatibility when reading. */
const FADE_IN_LEGACY = 'FadeIn';
const FADE_OUT_LEGACY = 'FadeOut';

/** True if source name is Fade In (new or legacy). Use when reading/comparing existing clips. */
export function isFadeInSource(sourceName: string): boolean {
  return sourceName === CLIP_SOURCES.FADE_IN || sourceName === FADE_IN_LEGACY;
}

/** True if source name is Fade Out (new or legacy). Use when reading/comparing existing clips. */
export function isFadeOutSource(sourceName: string): boolean {
  return sourceName === CLIP_SOURCES.FADE_OUT || sourceName === FADE_OUT_LEGACY;
}

/** True if source name is any fade clip (new or legacy). Use when reading/comparing existing clips. */
export function isFadeSource(sourceName: string): boolean {
  return isFadeInSource(sourceName) || isFadeOutSource(sourceName);
}

/** All source names that represent fade clips (new + legacy). Use for DB queries that exclude fade clips. */
export const FADE_SOURCE_NAMES: readonly string[] = [
  CLIP_SOURCES.FADE_IN,
  CLIP_SOURCES.FADE_OUT,
  FADE_IN_LEGACY,
  FADE_OUT_LEGACY
];